import { Link } from "react-router-dom";
import { ArrowUpRight, Ban, Compass, PenTool, ShieldCheck } from "lucide-react";
import { Fade, MaskedLines, Reveal } from "@/components/motion";
import { IMG, SITE } from "@/lib/site";

const PRINCIPLES = [
    {
        icon: Compass,
        title: "New projects only",
        text: "Newly launched, under-construction and ready-to-move projects from developers. Never resale, rentals or owner listings.",
    },
    {
        icon: ShieldCheck,
        title: "Direct connection",
        text: "Buyers reach developers on their own WhatsApp with a pre-filled enquiry. Sthaan never sits between the two.",
    },
    {
        icon: PenTool,
        title: "Design-led presentation",
        text: "Every project receives a crafted, project-specific showcase built only from genuine developer-supplied material.",
    },
    {
        icon: Ban,
        title: "Zero brokerage",
        text: "Sthaan charges buyers nothing, takes no commission on transactions, and is not a broker, agent or intermediary.",
    },
];

export default function About() {
    return (
        <div data-testid="about-page">
            <section className="relative flex min-h-[62vh] items-end overflow-hidden">
                <img
                    src={IMG.landscape_resort}
                    alt="Landscaped luxury development"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-obsidian/25" />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(100deg, rgba(250,248,244,0.97) 0%, rgba(250,248,244,0.82) 45%, rgba(250,248,244,0.3) 78%)",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
                <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-44 sm:px-8">
                    <Fade delay={0.1}>
                        <p className="eyebrow">About Sthaan</p>
                    </Fade>
                    <h1 className="mt-5 font-serif text-4xl font-light leading-[1.06] tracking-tight text-ivory sm:text-6xl">
                        <MaskedLines lines={["A better way to find", "what's being built next."]} />
                    </h1>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
                <div className="grid gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <p className="eyebrow">Our Purpose</p>
                            <h2 className="mt-4 font-serif text-3xl leading-tight text-ivory sm:text-4xl">
                                Sthaan means “place”. We help you find yours.
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-7">
                        <Reveal delay={0.1}>
                            <p className="text-lg leading-relaxed text-mist">
                                India builds beautifully — but discovering a new project
                                still means wading through classifieds, sponsored listings
                                and broker calls. Sthaan exists to change that.
                            </p>
                            <p className="mt-5 leading-relaxed text-mist">
                                We are a curated discovery platform for new residential and
                                commercial projects across India. Each project is presented
                                as a premium digital experience, crafted from material the
                                developer actually supplies. When something interests you,
                                you connect directly with the developer — no accounts, no
                                brokers, no brokerage.
                            </p>
                            <p className="mt-5 leading-relaxed text-mist">
                                Sthaan is a technology and discovery platform. We are not a
                                real-estate broker, agent or developer, and we do not
                                negotiate or execute property transactions.
                            </p>
                        </Reveal>
                    </div>
                </div>

                <div className="mt-16 grid gap-px overflow-hidden rounded-3xl border border-black/8 bg-black/5 sm:grid-cols-2 lg:grid-cols-4">
                    {PRINCIPLES.map((p) => (
                        <div key={p.title} className="bg-card p-8">
                            <p.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                            <h3 className="mt-5 font-serif text-xl text-ivory">{p.title}</h3>
                            <p className="mt-2.5 text-sm leading-relaxed text-mist">{p.text}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="border-y border-black/5 bg-surface/40">
                <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
                    <div className="grid gap-10 lg:grid-cols-2">
                        <Reveal>
                            <p className="eyebrow">Clarity</p>
                            <h2 className="mt-4 font-serif text-3xl text-ivory">
                                What Sthaan is not
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <ul className="space-y-4">
                                {[
                                    "Not a broker, agent or real-estate consultancy",
                                    "Not a classifieds site or OLX-style marketplace",
                                    "Not a resale or rental platform",
                                    "Not a transaction intermediary or payment platform",
                                    "Not a source of property, legal, financial or investment advice",
                                ].map((item) => (
                                    <li
                                        key={item}
                                        className="flex items-start gap-3 border-b border-black/5 pb-4 text-sm text-mist"
                                    >
                                        <Ban className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
                <Reveal>
                    <div className="grid items-center gap-10 rounded-3xl border border-black/8 bg-card p-10 sm:p-14 lg:grid-cols-2">
                        <div>
                            <p className="eyebrow">The Company</p>
                            <h2 className="mt-4 font-serif text-3xl text-ivory">
                                Built with intent, in India.
                            </h2>
                            <p className="mt-5 text-sm leading-relaxed text-mist">
                                Sthaan is a platform by Indexa Distribution Services Pvt.
                                Ltd., crafted in Gujarat for buyers and developers across
                                the country.
                            </p>
                            <p className="mt-6 text-xs leading-relaxed text-dim">
                                CIN: {SITE.cin}
                                <br />
                                {SITE.address}
                            </p>
                        </div>
                        <div className="flex flex-col items-start gap-4 lg:items-end">
                            <Link
                                to="/discover"
                                data-testid="about-discover-cta"
                                className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-obsidian transition-colors hover:bg-gold-bright"
                            >
                                Start discovering
                                <ArrowUpRight className="h-4 w-4" />
                            </Link>
                            <Link
                                to="/contact"
                                data-testid="about-contact-link"
                                className="link-line text-sm text-mist transition-colors hover:text-gold"
                            >
                                Contact the team
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
